import type {
  ActionDefinition,
  AdventureSceneDefinition,
  DialogueDefinition,
  DialogueNode,
  GameState,
  HotspotDefinition,
  MapLocationDefinition,
} from '../../domain/types';

export interface GameEvents {
  'ui:start': { continueGame: boolean };
  'ui:openLocation': { location: MapLocationDefinition };
  'ui:performAction': { hotspotId: string; action: ActionDefinition };
  'ui:dialogueChoice': { dialogue: DialogueDefinition; node: DialogueNode; choiceId: string };
  'ui:returnToMap': undefined;
  'ui:settingsChanged': undefined;
  'ui:debugHotspotChanged': { hotspot: HotspotDefinition };
  'ui:debugAnimationBounds': { visible: boolean };
  'ui:inputBlocked': { blocked: boolean };
  'state:changed': Readonly<GameState>;
  'loading:progress': { progress: number };
  'scene:map': undefined;
  'scene:adventure': AdventureSceneDefinition;
  'scene:hotspots': HotspotDefinition[];
  'hotspot:hover': { label: string; x: number; y: number };
  'hotspot:leave': undefined;
  'hotspot:actions': {
    hotspot: HotspotDefinition;
    actions: ActionDefinition[];
    x: number;
    y: number;
  };
  'dialogue:start': { dialogue: DialogueDefinition; node: DialogueNode };
  'dialogue:node': { dialogue: DialogueDefinition; node: DialogueNode };
  'dialogue:end': undefined;
  'message:show': { text: string; kind?: 'info' | 'item' | 'warning' };
  'audio:sfx': { soundId: string };
  'audio:theme': { theme: 'map' | 'forest' };
  'animation:command': { animationId: string };
  'debug:pointer': { x: number; y: number; normalizedX: number; normalizedY: number };
}

type Listener<T> = (payload: T) => void;

export class EventBus {
  readonly #listeners = new Map<keyof GameEvents, Set<Listener<never>>>();

  on<K extends keyof GameEvents>(event: K, listener: Listener<GameEvents[K]>): () => void {
    const listeners = this.#listeners.get(event) ?? new Set<Listener<never>>();
    listeners.add(listener as Listener<never>);
    this.#listeners.set(event, listeners);
    return () => listeners.delete(listener as Listener<never>);
  }

  emit<K extends keyof GameEvents>(
    event: K,
    ...args: GameEvents[K] extends undefined ? [] : [GameEvents[K]]
  ): void {
    for (const listener of this.#listeners.get(event) ?? []) {
      listener(args[0] as never);
    }
  }

  clear(): void {
    this.#listeners.clear();
  }
}
